import type { ReactNode } from 'react'
import { AlertTriangleIcon } from 'lucide-react'
import { useRouter } from '@tanstack/react-router'
import { ApiRequestError } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/lib/utils'

/** Plain-language message for any thrown value. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiRequestError) return error.message
  if (error instanceof TypeError) return 'We could not reach our servers. Check your connection and try again.'
  if (error instanceof Error) return error.message
  return 'Something went wrong.'
}

export function errorStatus(error: unknown): number | null {
  return error instanceof ApiRequestError ? error.status : null
}

export function LoadingState({ label = 'Loading', className }: { label?: string; className?: string }) {
  return (
    <div role="status" className={cn('flex items-center justify-center gap-2 py-16 text-muted-foreground', className)}>
      <Spinner aria-hidden />
      <span>{label}…</span>
    </div>
  )
}

/** Error block with a retry that reruns the route loaders. */
export function ErrorState({
  error,
  title = 'Something went wrong',
  onRetry,
  className,
  children,
}: {
  error: unknown
  title?: string
  onRetry?: () => void
  className?: string
  children?: ReactNode
}) {
  const router = useRouter()
  return (
    <div role="alert" className={cn('mx-auto flex max-w-md flex-col items-center gap-3 py-16 text-center', className)}>
      <AlertTriangleIcon className="size-8 text-destructive" aria-hidden />
      <h2 className="text-lg font-semibold">{title}</h2>
      <p className="text-muted-foreground">{errorMessage(error)}</p>
      {children}
      <Button variant="outline" onClick={onRetry ?? (() => router.invalidate())}>
        Try again
      </Button>
    </div>
  )
}

/** Inline form-level error, announced to screen readers. */
export function FormError({ message, className }: { message: string | null; className?: string }) {
  if (!message) return null
  return (
    <p role="alert" className={cn('text-sm text-destructive', className)}>
      {message}
    </p>
  )
}
